import { DataType, QueryFactory } from '@lmt/shared-types';
import { GDTMappingRule } from '@lmt/shared-types';

import { AutoSizedTableWrapper } from '@lmt/unicorn-shared/app/components/common/AutoSizedTableWrapper';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  useDictionaryFieldsFilterFunc,
  useFieldNamesFilterFunc,
} from '@lmt/unicorn-shared/app/components/hl7/mappings/common/common';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import { useDataGridExportHandler } from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';

import React, { FC, useMemo, useRef, useState } from 'react';
import {
  useDataGrid,
  DXG,
  DataGrid,
  IEditingProps,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { ExpressionBuilderField } from '@lmt/unicorn-shared/app/components/hl7/mappings/edit/components/MappingTable';
import { GdtMessageSegments } from '@lmt/unicorn-shared/data/gdtMessageCodeNames';
import { MonacoExpressionBuilder } from '@lmt/unicorn-shared/app/common/Monaco/MonacoExpressionBuilder';
import { expressionFunctions } from '@lmt/expressions/src/expressions/helpers/expression-functions';
import { useEventCallback } from '@mui/material';
import { mapExcelRowToDataGrid } from '@lmt/unicorn-shared/app/helpers/excel-to-grid-helper';
import { isEmpty } from '@lmt/unicorn-shared/app/components/measurements/copy-datablock-field/common';
import {
  allowOnlySingleNewRow,
  getAllRows,
  useCachedDataSourceWhenEditing,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/grid-helper';
import { readFileAsExcel } from '@lmt/unicorn-shared/app/helpers/file-helper';
import loader from '@lmt/unicorn-shared/src/app/animations/loader.gif';

type Props = {
  mappingId: number;
};

type Row = {
  id: string;
} & GDTMappingRule;

const nameof = nameofFactory<GDTMappingRule>();

const tables = ['Patient', 'Visit', 'Case'];

export const MappingTable: FC<Props> = (props) => {
  useDataGrid();

  const i18n = useScopedTranslation('gdt.mapping');

  const dispatch = useUniDispatch();
  const modals = useModal();
  const {
    data: activeMapping,
    isLoading,
    refetch: refetch,
  } = QueryFactory.GDTMappingQuery.useGetSingleQuery(props.mappingId);

  const { data: queryFields } =
    QueryFactory.GDTMappingQuery.useGetFieldsQuery();

  const inputFileRef = useRef<HTMLInputElement>(null);

  const tableNames = useMemo(() => {
    if (!queryFields?.length) {
      return [];
    }
    // unique table names
    const set = new Set(
      queryFields
        .filter((x) => tables.includes(x.tableName) || !!x.dataBlockTypeId)
        .map((x) => x.tableName),
    );
    return [...set.values()];
  }, [queryFields]);

  const fieldNamesFilterFunc = useFieldNamesFilterFunc(queryFields, tableNames);
  const dictionaryFieldsFilterFunc = useDictionaryFieldsFilterFunc(queryFields);

  const dataGridRef = useRef<DataGrid<Row, string>>(null);

  const mappingRules = useMemo(
    () => activeMapping?.rules.map((x) => ({ ...x, id: createId() })),
    [activeMapping],
  );
  const dataSource = useCachedDataSourceWhenEditing(dataGridRef, mappingRules);

  const [isExcelLoading, setIsExcelLoading] = useState(false);
  async function onImportExcelFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target!.files![0];
    if (!file) {
      return;
    }
    setIsExcelLoading(true);

    try {
      const values = await readFileAsExcel(file);
      const dataGrid = dataGridRef.current!.instance();
      for (let i = 2; i < values.length; i++) {
        const row = values[i];
        try {
          const newRow = await mapExcelRowToDataGrid<Row>(dataGrid, row);
          newRow.id = createId();
          const initNewRow = (e: any) => {
            Object.assign(e.data, newRow);
          };
          dataGrid.on('initNewRow', initNewRow);
          await dataGrid.addRow();

          dataGrid.off('initNewRow', initNewRow);
        } catch (e) {
          await modals.showError({
            text: `Error parsing excel row ${i}: ${convertToErrorString(e)}`,
          });
          return;
        }
      }
    } finally {
      setIsExcelLoading(false);
      // to allow choosing the same file again
      inputFileRef.current!.value = '';
    }
  }

  const onExportHandler = useDataGridExportHandler({
    fileName: 'GDT mappings',
  });

  const onTableChange = useEventCallback((data: Row, value: string | null) => {
    data.fieldName = '';
    data.dictionaryField = null;
    data.table = value ?? '';
  });

  const onDictionaryFieldChange = useEventCallback(
    (data: Row, value: string | null) => {
      data.fieldName = '';
      data.dictionaryField = value;
    },
  );

  const onFieldNameChange = useEventCallback(
    (data: Row, value: string, existingRow: Row) => {
      data.fieldName = value;
      const queryField = queryFields?.find(
        (x) => x.fieldName === value && x.tableName === existingRow.table,
      );
      if (!queryField?.dataBlockTypeId) {
        return;
      }
      data.dataBlockTypeId = queryField.dataBlockTypeId;
      data.dataBlockVariant = queryField.dataBlockVariant;
    },
  );

  return (
    <Loading
      loading={isLoading || tableNames.length === 0 || isExcelLoading}
      title={isExcelLoading ? i18n.t('loading_excel') : i18n.t('loading')}
    >
      <input
        type="file"
        id="importExcelFile"
        ref={inputFileRef}
        className={'hidden'}
        onChange={onImportExcelFile}
      />
      {tableNames.length > 0 && (
        <AutoSizedTableWrapper>
          <DXG.DataGrid
            ref={dataGridRef}
            export={{
              enabled: true,
            }}
            loadPanel={{ enabled: true, indicatorOptions: { src: loader } }}
            onSaving={async (ev) => {
              const allRows = getAllRows(ev);

              ev.promise = (async () => {
                dataGridRef.current?.instance().beginCustomLoading('');
                try {
                  await QueryFactory.GDTMappingClient.patch(props.mappingId, {
                    rules: allRows,
                  });
                  await refetch();

                  ev.cancel = false;
                } catch (e) {
                  ev.cancel = true;
                  await modals.showError({ text: convertToErrorString(e) });
                } finally {
                  dataGridRef.current?.instance().endCustomLoading();
                }
              })();
            }}
            onExporting={onExportHandler}
            onToolbarPreparing={(e) => {
              e.toolbarOptions!.items!.push({
                location: 'after',
                widget: 'dxButton',
                options: {
                  width: 136,
                  text: i18n.t('import_button'),
                  onClick: () => inputFileRef.current!.click(),
                },
              });
              allowOnlySingleNewRow(e);
            }}
            dataSource={dataSource}
            keyExpr={'id'}
            showBorders={true}
            style={{ width: '100%' }}
            onInitNewRow={(e) => {
              const data = e.data as Row;
              data.id = createId();
              if (isEmpty(e.data.enabled)) {
                data.enabled = true;
              }
              data.default = false;
            }}
          >
            <DXG.Paging enabled={false} />
            <DXG.Editing
              mode="batch"
              allowUpdating={true}
              allowAdding={true}
              allowDeleting={allowDeleting}
              selectTextOnEditStart={true}
              startEditAction="click"
              newRowPosition="pageTop"
            />
            <DXG.Sorting mode="single" />
            <DXG.Column dataField="id" sortOrder="desc" visible={false} />
            <DXG.Column
              width={'25%'}
              dataField={nameof('table')}
              dataType="string"
              caption={i18n.t('edit.table')}
              sortIndex={0}
              defaultSortOrder="asc"
              setCellValue={onTableChange}
            >
              <DXG.Lookup dataSource={tableNames} />
              <DXG.RequiredRule />
            </DXG.Column>
            <DXG.Column
              width={'25%'}
              dataField={nameof('dictionaryField')}
              caption={i18n.t('edit.dictionary_field')}
              dataType="string"
              sortIndex={1}
              defaultSortOrder="asc"
              setCellValue={onDictionaryFieldChange}
            >
              <DXG.Lookup
                dataSource={dictionaryFieldsFilterFunc as any}
                displayExpr="fieldName"
                valueExpr="fieldName"
                allowClearing={true}
              />
            </DXG.Column>
            <DXG.Column
              width={'25%'}
              dataField={nameof('fieldName')}
              caption={i18n.t('edit.field_name')}
              dataType="string"
              sortIndex={1}
              defaultSortOrder="asc"
              setCellValue={onFieldNameChange}
            >
              <DXG.RequiredRule />
              <DXG.Lookup
                dataSource={fieldNamesFilterFunc as any}
                displayExpr="fieldName"
                valueExpr="fieldName"
              />
            </DXG.Column>
            <DXG.Column
              width={'40%'}
              dataField={nameof('gdtCode')}
              caption={i18n.t('edit.gdt_code')}
              dataType="string"
              editCellComponent={GdtExpressionBuilder}
            >
              <DXG.RequiredRule />
            </DXG.Column>
            <DXG.Column
              width={80}
              dataField={nameof('enabled')}
              caption={i18n.t('edit.enabled')}
              dataType="boolean"
            />
          </DXG.DataGrid>
        </AutoSizedTableWrapper>
      )}
    </Loading>
  );
};

const allowDeleting: IEditingProps['allowDeleting'] = (options) =>
  !options.row.data.default;

const _gdtFieldNames: ExpressionBuilderField[] = [];
const GdtExpressionBuilder = React.memo(function GdtExpressionBuilder({
  data,
}: {
  data: any;
}) {
  if (_gdtFieldNames.length === 0) {
    GdtMessageSegments.forEach((segment) => {
      const id = segment.id;
      _gdtFieldNames.push({
        caption: id + ' ' + segment.description,
        value: id,
        dataType: DataType.Text,
        score: 1,
      });
    });
  }
  const intellisenseContent = [
    ...expressionFunctions.map((x) => `declare function ${x.signature}`),
    ..._gdtFieldNames.map((x) => `declare const ${x.value} = "${x.caption}"`),
  ].join('\n');
  return (
    <MonacoExpressionBuilder
      defaultExpressionValue={data.value}
      onExpressionChanged={(e: string) => data.setValue(e)}
      intellisenseContent={intellisenseContent}
      disableValidation={true}
      height={40}
    />
  );
});
